import { CSSProperties } from "react";
import { TemplateItem } from "types";
import { Mask } from "../types";

/** No crop — the mask is the whole item box. */
export const DEFAULT_MASK: Mask = { x: 0, y: 0, width: 1, height: 1 };
/** Smallest mask size (fraction of the box) so it can't collapse to nothing. */
export const MIN_MASK_SIZE = 0.05;

/** Whether the mask actually crops anything (an ellipse always clips corners). */
export const isMasked = (mask?: Mask): mask is Mask =>
  !!mask &&
  (mask.shape === "ellipse" ||
    mask.x > 0 ||
    mask.y > 0 ||
    mask.width < 1 ||
    mask.height < 1);

/** Clamp a mask to stay inside the box with a minimum size on each axis. */
export const clampMask = (mask: Mask): Mask => {
  const width = Math.min(Math.max(mask.width, MIN_MASK_SIZE), 1);
  const height = Math.min(Math.max(mask.height, MIN_MASK_SIZE), 1);
  return {
    ...mask, // preserve `shape`
    width,
    height,
    x: Math.min(Math.max(mask.x, 0), 1 - width),
    y: Math.min(Math.max(mask.y, 0), 1 - height),
  };
};

export const clampAxis = (t: number, lo: number, hi: number) =>
  lo > hi ? (lo + hi) / 2 : Math.min(Math.max(t, lo), hi);

/**
 * The crop's canvas AABB half-extents and its center offset from the box
 * top-left, for a box of `boxWidth`×`boxHeight` rotated by `rotationDeg` about
 * its center. Used to keep the crop within the canvas under rotation.
 */
export const getMaskCanvasGeometry = (
  mask: Mask,
  boxWidth: number,
  boxHeight: number,
  rotationDeg: number
): {
  extentX: number;
  extentY: number;
  centerOffX: number;
  centerOffY: number;
} => {
  const rad = (rotationDeg * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const halfW = boxWidth / 2;
  const halfH = boxHeight / 2;
  const hw = (mask.width / 2) * boxWidth;
  const hh = (mask.height / 2) * boxHeight;
  const extentX =
    mask.shape === "ellipse"
      ? Math.sqrt(hw * hw * cos * cos + hh * hh * sin * sin)
      : hw * Math.abs(cos) + hh * Math.abs(sin);
  const extentY =
    mask.shape === "ellipse"
      ? Math.sqrt(hw * hw * sin * sin + hh * hh * cos * cos)
      : hw * Math.abs(sin) + hh * Math.abs(cos);
  const u = (mask.x + mask.width / 2) * boxWidth - halfW;
  const v = (mask.y + mask.height / 2) * boxHeight - halfH;
  return {
    extentX,
    extentY,
    centerOffX: halfW + cos * u - sin * v,
    centerOffY: halfH + sin * u + cos * v,
  };
};

/**
 * Build a centered circular mask inscribed in the current mask (or the whole
 * box if none). The radius is the largest circle that fits, kept a true pixel
 * circle given the box dimensions.
 */
export const toCircularMask = (
  mask: Mask | undefined,
  boxWidth: number,
  boxHeight: number
): Mask => {
  const base = mask ?? DEFAULT_MASK;
  const cx = base.x + base.width / 2;
  const cy = base.y + base.height / 2;
  const rPx = Math.min(base.width * boxWidth, base.height * boxHeight) / 2;
  const width = (2 * rPx) / boxWidth;
  const height = (2 * rPx) / boxHeight;
  return {
    shape: "ellipse",
    x: cx - width / 2,
    y: cy - height / 2,
    width,
    height,
  };
};

/**
 * `clip-path` that hides everything outside the mask — `inset()` for a
 * rectangle, `ellipse()` for a circle/ellipse — so only the cropped region of
 * the media shows.
 */
export const getMaskClipStyle = (mask?: Mask): CSSProperties => {
  if (!isMasked(mask)) return {};
  if (mask.shape === "ellipse") {
    const rx = (mask.width / 2) * 100;
    const ry = (mask.height / 2) * 100;
    const cx = (mask.x + mask.width / 2) * 100;
    const cy = (mask.y + mask.height / 2) * 100;
    return { clipPath: `ellipse(${rx}% ${ry}% at ${cx}% ${cy}%)` };
  }
  const top = mask.y * 100;
  const right = (1 - mask.x - mask.width) * 100;
  const bottom = (1 - mask.y - mask.height) * 100;
  const left = mask.x * 100;
  return { clipPath: `inset(${top}% ${right}% ${bottom}% ${left}%)` };
};

/**
 * Reverse of `generateCropMask`: read a Rectangle/Circle crop mask off the item
 * graphics into a `{x, y, width, height, shape}` sub-region of the item box.
 * Returns `undefined` when there's no (cropping) mask. Shared by every mapper
 * so the crop is loaded — and therefore renders — in all template previews,
 * not just the editable storyboard canvas.
 */
export const extractMask = (
  graphics: TemplateItem["graphics"]
): Mask | undefined => {
  const mask = "mask" in graphics ? graphics.mask : null;
  const shapeType = mask?.shape.type;
  if (!mask || (shapeType !== "Rectangle" && shapeType !== "Circle"))
    return undefined;

  let width: number | undefined;
  let height: number | undefined;
  let x = 0;
  let y = 0;
  for (const attr of mask.attributes) {
    if (attr.type === "Width") width = attr.rate;
    else if (attr.type === "Height") height = attr.rate;
    else if (attr.type === "Position" && attr.position.type === "Align") {
      x = attr.position.horizontal_ratio ?? 0;
      y = attr.position.vertical_ratio ?? 0;
    }
  }
  if (width == null || height == null) return undefined;
  const candidate = clampMask({
    x,
    y,
    width,
    height,
    shape: shapeType === "Circle" ? "ellipse" : "rectangle",
  });
  return isMasked(candidate) ? candidate : undefined;
};
